import React, { useEffect, useRef, useState } from 'react'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import { dateToDashString } from '../../utils/dateUtility'
import { useLocation, useNavigate, useParams } from 'react-router'
import SearchBar from '../Search/SearchBar'
import { EnrollmentInviteStatusEnum, OrderByDirection } from '../../swagger'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import TextButton, { TextButtonVariant } from '../Buttons/TextButton'
import { SnackbarSeverity } from '../Alerts/SnackbarAlert'
import { extractedErrorObject, families, invitesApi } from '../../api/swagger'
import EnrollmentInviteRowInfo from '../Interfaces/EnrollmentInviteRowInfo'
import DropDown, { DropDownVariant } from '../Menus/DropDown'
import { useSnackbarContext } from '../Context/SnackbarContext'
import { validateEmailText } from '../../helpers/validateEmail'
import useEmailMessage from '../../hooks/useEmailMessage'
import Box from '@mui/material/Box'
import { styled } from '@mui/system'
import { useAuth } from '../Routes/AuthProvider'
import {
  ENROLLMENT_INVITES_MIN_SEARCH_LENGTH,
  enrollmentInviteStatusFilterOptions,
  useProgramDetailsContext,
} from '../Context/ProgramDetailsContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import useLoadingContext from '../../hooks/useLoadingContext'
import {
  ActionableTable,
  ActionableTableColumn,
} from '../Table/ActionableTable'
import { GridCellParams, GridValueFormatterParams } from '@mui/x-data-grid'
import { Action } from '../Table/RowActions'
import { PaginationResponse } from '../../swagger/models/PaginationResponse'
import { GlobalStyles } from '@mui/material'
import EmptyPage from '../Elements/EmptyPage'
import { ProgramDetailsTab } from './ProgramDetails'
import EnrollmentsInviteConfirmationModal, {
  EnrollmentInviteConfirmationModalVariant,
} from '../Modals/EnrollmentsInviteConfirmationModal'

const fileName = 'EnrollmentsInviteTable'

const Section = styled('section')(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}))

export interface InputProperties {
  value: string
  error: string
  valid: boolean
  initialValue: string
}

interface EnrollmentsInviteTableProps {
  isTabInvitesTourCompleted?: boolean
}

export const EnrollmentsInviteTable: React.FC<EnrollmentsInviteTableProps> = ({
  isTabInvitesTourCompleted,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { validationMessages } = useEmailMessage()
  const { setSnackbarSeverity, setSnackbarMessage, setSnackbarState } =
    useSnackbarContext()
  const { permissionAbility } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const showOnDesktop = useShowOnDesktop()
  const familyNameActingOn = useRef('')
  const invitationIdActingOn = useRef('')
  const {
    EnrollmentsInviteTable: {
      resendInvitation: resendInvitationLoadingId,
      cancelInvitation: cancelInvitationLoadingId,
      declineInvitation: declineInvitationLoadingId,
      fetchEnrollmentInvites: fetchEnrollmentInvitesBaseLoadingId,
    },
    ProgramDetails: { fetchProgram: fetchProgramDetailsLoadingId },
  } = useLoadingIds()

  const fetchEnrollmentInvitesLoadingId = `${fileName}-${fetchEnrollmentInvitesBaseLoadingId}`

  const InitialStateSentEmail: InputProperties = {
    value: '',
    error: '',
    valid: false,
    initialValue: '',
  }

  /** State Objects */
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [confirmationModalVariant, setConfirmationModalVariant] = useState(
    EnrollmentInviteConfirmationModalVariant.Resend
  )

  const [sentEmail, setSentEmail] = useState<InputProperties>(
    InitialStateSentEmail
  )
  const { programKey } = useParams<{ programKey: string }>()
  const programId = parseInt(`${programKey}`)

  const {
    updateEnrollmentInviteFilters,
    updateSearchEnrollmentInvites,
    handleEnrollmentInvitesPagination,
    fetchEnrollmentInvites: fetchInvites,

    searchEnrollmentInvites,
    enrollmentInvites,
    enrollmentInviteFilters,
    enrollmentInvitesPagination,
    isSearchingOrFilteringEnrollmentInvites,
  } = useProgramDetailsContext()

  const fetchEnrollmentInvites = async () => {
    await fetchInvites(programId)
  }

  const { addLoadingIds, triggerFetch } = useLoadingContext({
    loadingId: fetchEnrollmentInvitesLoadingId,
    asyncFunction: fetchEnrollmentInvites,
  })

  useEffect(() => {
    triggerFetch()
  }, [
    searchEnrollmentInvites,
    enrollmentInviteFilters,
    enrollmentInvitesPagination.page,
    enrollmentInvitesPagination.pageSize,
    enrollmentInvitesPagination.orderBy,
    triggerFetch,
  ])

  const refetchEnrollmentsData = () => {
    if (permissionAbility.can('view', 'Family')) {
      triggerFetch()
    }
  }

  const getLabelForInviteStatus = (options: {
    inviteStatus?: string
  }): string => {
    let label = ''
    switch (options.inviteStatus) {
      case enrollmentInviteStatusFilterOptions.AllStatus:
      default:
        label = t(
          'EnrollmentsInvite.FilterOption.AllInviteStatus',
          'All Invite Statuses'
        )
        break
      case enrollmentInviteStatusFilterOptions.InProgress:
        label = t('EnrollmentsInvite.FilterOption.InProgress', 'In Progress')
        break
      case enrollmentInviteStatusFilterOptions.Complete:
        label = t('EnrollmentsInvite.FilterOption.Complete', 'Accepted')
        break
      case enrollmentInviteStatusFilterOptions.Cancelled:
        label = t('EnrollmentsInvite.FilterOption.Cancelled', 'Cancelled')
        break
      case enrollmentInviteStatusFilterOptions.Declined:
        label = t('EnrollmentsInvite.FilterOption.Declined', 'Declined')
        break
    }
    return label
  }

  const addPointerCell = (params: GridCellParams): string => {
    if (
      !!params.row.primaryContactId &&
      params.row.enrollmentInviteStatus === EnrollmentInviteStatusEnum.Complete
    ) {
      // this custom class allows us to add the cursor pointer to the cells
      return 'MuiDataGrid-cell-pointer'
    }
    return ''
  }

  // action Headers
  const tableActionHeaders: ActionableTableColumn[] = [
    {
      fieldName: 'familyName',
      columnHeaderName: t(
        'Enrollments.Invite.Table.FamilyName.Header',
        'Family Name'
      ),
      cellClassName: addPointerCell,
    },
    {
      fieldName: 'sentToEmail',
      columnHeaderName: t(
        'Enrollments.Invite.Table.SentEmail.Header',
        'Sent Email'
      ),
      cellClassName: addPointerCell,
      sortable: true,
      sortingOrder: ['asc', 'desc'],
    },
    {
      fieldName: 'inviteDate',
      columnHeaderName: t(
        'Enrollments.Invite.Table.InviteDate.Header',
        'Invite Date'
      ),
      cellClassName: addPointerCell,
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        return dateToDashString(params.value)
      },
      sortable: true,
      sortingOrder: ['asc', 'desc'],
    },
    {
      fieldName: 'resentDate',
      columnHeaderName: t(
        'Enrollments.Invite.Table.ResentDate.Header',
        'Re-sent Date'
      ),
      cellClassName: addPointerCell,
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        return !!params.value ? dateToDashString(params.value) : ''
      },
      description: t(
        'Enrollments.Invite.Table.ResentDate.HeaderDescription',
        'Date when this invitation was last resent'
      ),
    },
    {
      fieldName: 'offeredSpots',
      columnHeaderName: t(
        'Enrollments.Invite.Table.OfferedSpot.Header',
        'Offered Spots'
      ),
      cellClassName: addPointerCell,
      description:
        t(
          'Enrollments.Invite.Table.OfferedSpot.HeaderDescription',
          'Total Offered Spots: '
        ) +
        enrollmentInvites.reduce((acc, curr) => {
          return acc + curr.offeredSpots
        }, 0),
    },
    {
      fieldName: 'skippedSpots',
      columnHeaderName: t(
        'Enrollments.Invite.Table.SkippedSpots.Header',
        'Skipped Spots'
      ),
      cellClassName: addPointerCell,
      description:
        t(
          'Enrollments.Invite.Table.OfferedSpot.HeaderDescription',
          'Total Skipped Spots: '
        ) +
        enrollmentInvites.reduce((acc, curr) => {
          return acc + (curr?.skippedSpots ?? 0)
        }, 0),
    },
    {
      fieldName: 'enrollmentInviteStatus',
      columnHeaderName: t(
        'Enrollments.Invite.Table.InviteStatus.Header',
        'Status'
      ),
      cellClassName: addPointerCell,
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        return getLabelForInviteStatus({
          inviteStatus: params.value,
        })
      },
    },
  ]

  const handleCancel = (row: EnrollmentInviteRowInfo) => {
    setConfirmationModalVariant(EnrollmentInviteConfirmationModalVariant.Cancel)
    setShowConfirmationModal(true)
    familyNameActingOn.current = row.familyName
    invitationIdActingOn.current = row.rowId
  }

  const handleDecline = (row: EnrollmentInviteRowInfo) => {
    setConfirmationModalVariant(
      EnrollmentInviteConfirmationModalVariant.Decline
    )
    setShowConfirmationModal(true)
    familyNameActingOn.current = row.familyName
    invitationIdActingOn.current = row.rowId
  }

  const clickableCell = (
    enrollmentInvite: EnrollmentInviteRowInfo,
    field: string
  ) => {
    if (field !== 'actions') {
      !!enrollmentInvite.primaryContactId &&
        enrollmentInvite.enrollmentInviteStatus ===
          EnrollmentInviteStatusEnum.Complete &&
        navigateToFamilyProfile(enrollmentInvite.primaryContactId)
    }
  }

  const handlePagination = (pagination: PaginationResponse) => {
    if (enrollmentInvitesPagination.page < 1) return
    handleEnrollmentInvitesPagination?.(pagination)
  }

  const rowActions: Action<EnrollmentInviteRowInfo>[] = [
    {
      actionName: t(
        'Enrollments.Invite.Table.RowAction.EditAndResend',
        'Edit & Resend'
      ),
      actionKey: 'edit',
      actionFunction: (enrollmentRow) =>
        handleResendClick(
          enrollmentRow ?? ([] as unknown as EnrollmentInviteRowInfo)
        ),
      hide: (enrollmentRow) => {
        const isEditAndResend =
          enrollmentRow?.enrollmentInviteStatus ===
            EnrollmentInviteStatusEnum.InProgress ||
          enrollmentRow?.enrollmentInviteStatus ===
            EnrollmentInviteStatusEnum.Declined
        return !isEditAndResend
      },
    },
    {
      actionName: t('Enrollments.Invite.Table.RowAction.Decline', 'Decline'),
      actionKey: 'decline',
      actionFunction: (enrollmentRow) =>
        handleDecline(
          enrollmentRow ?? ([] as unknown as EnrollmentInviteRowInfo)
        ),
      hide: (enrollmentRow) =>
        !(
          enrollmentRow?.enrollmentInviteStatus ===
          EnrollmentInviteStatusEnum.InProgress
        ),
    },
  ]
  if (permissionAbility.can('allowCancelInvite', 'Family')) {
    rowActions.push({
      actionName: t('Enrollments.Invite.Table.RowAction.Cancel', 'Cancel'),
      actionKey: 'cancel',
      actionFunction: (enrollmentRow) =>
        handleCancel(
          enrollmentRow ?? ([] as unknown as EnrollmentInviteRowInfo)
        ),
      hide: (enrollmentRow) =>
        enrollmentRow?.enrollmentInviteStatus ===
        EnrollmentInviteStatusEnum.Cancelled,
    })
  }

  /** Methods */
  const navigateToFamilyProfile = (userId: number) => {
    navigate(
      {
        pathname: `/family-profile/${userId}`,
      },
      {
        /** Navigation Options */
      }
    )
  }

  const handleSearch = (searchText: string) => {
    if (searchText.length >= ENROLLMENT_INVITES_MIN_SEARCH_LENGTH) {
      updateSearchEnrollmentInvites(searchText)
    }
    if (searchText.length === 0) {
      updateSearchEnrollmentInvites('')
    }
  }

  const handleResendInvitation = async () => {
    let snackbarMessage: string
    let snackbarSeverity: SnackbarSeverity

    if (!sentEmail.valid) return
    try {
      await families.resendInvitation({
        enrollmentInviteUuid: invitationIdActingOn.current,
        body:
          sentEmail.initialValue === sentEmail.value
            ? {}
            : { sentToEmail: sentEmail.value },
      })
      refetchEnrollmentsData?.()
      const successMessage = t(
        'EnrollmentsInviteTable.ResendInvitation',
        'Invitation resent'
      )
      snackbarMessage = successMessage
      snackbarSeverity = SnackbarSeverity.Success
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown',
        message: (e as unknown as Error).message,
      }
      snackbarMessage = errorObject.message
      snackbarSeverity = SnackbarSeverity.Error
    }
    setSnackbarState?.(true)
    setSnackbarMessage?.(snackbarMessage)
    setSnackbarSeverity?.(snackbarSeverity)

    setShowConfirmationModal(false)
  }

  useLoadingContext({
    asyncFunction: handleResendInvitation,
    loadingId: resendInvitationLoadingId,
  })

  const sharedDropDownProps = {
    variant: DropDownVariant.SortAndFilter,
    fullWidth: !showOnDesktop,
  }

  const handleCancelInvitation = async () => {
    try {
      await families.cancelInvitation({ uuid: invitationIdActingOn.current })
      refetchEnrollmentsData?.()
      const successMessage = t(
        'EnrollmentsInviteTable.CancelInvitation',
        'Invitation cancelled'
      )
      setSnackbarState?.(true)
      setSnackbarMessage?.(successMessage)
      setSnackbarSeverity?.(SnackbarSeverity.Success)
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown',
        message: (e as unknown as Error).message,
      }
      setSnackbarState?.(true)
      setSnackbarMessage?.(errorObject.message)
      setSnackbarSeverity?.(SnackbarSeverity.Error)
    } finally {
      setShowConfirmationModal(false)
    }
  }

  useLoadingContext({
    asyncFunction: handleCancelInvitation,
    loadingId: cancelInvitationLoadingId,
  })

  const handleDeclineInvitation = async () => {
    try {
      await invitesApi.declineInvitation({ uuid: invitationIdActingOn.current })
      refetchEnrollmentsData?.()
      const successMessage = t(
        'EnrollmentsInviteTable.DeclineInvitation',
        'Invitation declined'
      )
      setSnackbarState?.(true)
      setSnackbarMessage?.(successMessage)
      setSnackbarSeverity?.(SnackbarSeverity.Success)

      // re-fetch program details
      addLoadingIds([fetchProgramDetailsLoadingId])
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown',
        message: (e as unknown as Error).message,
      }
      setSnackbarState?.(true)
      setSnackbarMessage?.(errorObject.message)
      setSnackbarSeverity?.(SnackbarSeverity.Error)
    } finally {
      setShowConfirmationModal(false)
    }
  }

  useLoadingContext({
    asyncFunction: handleDeclineInvitation,
    loadingId: declineInvitationLoadingId,
  })

  const handleConfirmationCancel = async () => {
    setShowConfirmationModal(false)
    setSentEmail(InitialStateSentEmail)
  }

  const handleInviteStatusFilterSelection = (
    _: string,
    __: string,
    selectionOptionId: string | number
  ) => {
    updateEnrollmentInviteFilters({
      ...enrollmentInviteFilters,
      inviteStatus: `${selectionOptionId}`,
    })
  }

  const handleChangeSentEmail = (email: string) => {
    email = email.trim()
    const errorEmailCode = validateEmailText(email)

    setSentEmail({
      ...sentEmail,
      value: email,
      error: validationMessages(errorEmailCode),
      valid: !errorEmailCode,
    })
  }

  const handleResendClick = (enrollmentRow: EnrollmentInviteRowInfo) => {
    setConfirmationModalVariant(EnrollmentInviteConfirmationModalVariant.Resend)
    setShowConfirmationModal(true)
    familyNameActingOn.current = enrollmentRow.familyName
    invitationIdActingOn.current = enrollmentRow.rowId
    setSentEmail({
      ...sentEmail,
      value: enrollmentRow.sentToEmail,
      initialValue: enrollmentRow.sentToEmail,
      valid: true,
    })
  }

  const changeTab = () => {
    // we are going to reload only if we are on mobile
    // At the finish of the tour we need to re-render the component again so we change tabs to do it
    // Since we are using only the columns to apply the tour (joyRide) and avoid breaking the layout
    if (!showOnDesktop) {
      navigate(location.pathname, {
        state: {
          tabToChange: ProgramDetailsTab.programDetails,
          tabFrom: ProgramDetailsTab.invites,
        },
      })
    }
  }

  // Take necessary columns for tour in mobile version
  let actionableTableColumns = tableActionHeaders

  if (!isTabInvitesTourCompleted && !showOnDesktop) {
    actionableTableColumns = tableActionHeaders.slice(-1)
  }

  const rowActionsTourSteps = [
    {
      target: '.featureTour',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: t(
        'SpeedDialMenu.Tour.StepZero.Content',
        'Click here to see the Edit and Resend Button.'
      ),
      disableBeacon: true,
      disableScrolling: true,
    },
  ]

  const copyTableToClipboard = () => {
    const headerString =
      actionableTableColumns
        .map((column) => `${column.columnHeaderName}`)
        .join('\t') + '\n'
    const tableString = enrollmentInvites
      .map((e) =>
        [
          e.familyName,
          e.sentToEmail,
          dateToDashString(e.inviteDate),
          !!e.resentDate ? dateToDashString(e.resentDate) : '',
          e.offeredSpots,
          e.skippedSpots,
          getLabelForInviteStatus({
            inviteStatus: e.enrollmentInviteStatus,
          }),
        ].join('\t')
      )
      .join('\n')
    navigator.clipboard.writeText(headerString + tableString)

    setSnackbarMessage(
      t(
        'EnrollmentsInviteTable.CopiedToClipboard',
        'Copied table to clipboard!'
      )
    )
    setSnackbarSeverity(SnackbarSeverity.Success)
    setSnackbarState(true)
  }

  return (
    <>
      {/* add class with pointer effect fot the cell */}
      <GlobalStyles
        styles={{
          '.MuiDataGrid-cell-pointer': {
            cursor: 'pointer',
          },
        }}
      />
      <section aria-label="invites">
        {(isSearchingOrFilteringEnrollmentInvites &&
          enrollmentInvites.length === 0) ||
        enrollmentInvites.length > 0 ? (
          <>
            <EnrollmentsInviteConfirmationModal
              isOpen={showConfirmationModal}
              variant={confirmationModalVariant}
              familyNameActingOn={familyNameActingOn.current}
              sentEmail={sentEmail}
              handleChangeSentEmail={handleChangeSentEmail}
              handleConfirmationCancel={handleConfirmationCancel}
              resendInvitationLoadingId={resendInvitationLoadingId}
              cancelInvitationLoadingId={cancelInvitationLoadingId}
              declineInvitationLoadingId={declineInvitationLoadingId}
            />
            <Section
              aria-label="searchSortAndFilter"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                [theme.breakpoints.down('sm')]: {
                  alignItems: 'center',
                },
              }}
            >
              <Box display="flex">
                <SearchBar handleSearch={handleSearch} />
                <Box
                  sx={{
                    marginLeft: theme.spacing(2),
                    marginTop: theme.spacing(0.25),
                    [theme.breakpoints.down('sm')]: {
                      marginLeft: theme.spacing(0),
                      width: '50%',
                    },
                  }}
                >
                  <DropDown
                    formControlProps={{
                      [theme.breakpoints.down('sm')]: {
                        margin: theme.spacing(0),
                        width: '100%',
                      },
                    }}
                    id="inviteStatusFilter"
                    menuOptions={Object.values(
                      enrollmentInviteStatusFilterOptions
                    ).map((status) => {
                      return {
                        name: getLabelForInviteStatus({ inviteStatus: status }),
                        id: status,
                      }
                    })}
                    defaultValue={getLabelForInviteStatus({
                      inviteStatus: enrollmentInviteFilters.inviteStatus,
                    })}
                    value={getLabelForInviteStatus({
                      inviteStatus: enrollmentInviteFilters.inviteStatus,
                    })}
                    handleSelection={handleInviteStatusFilterSelection}
                    {...{ ...sharedDropDownProps, fullWidth: false }}
                  />
                </Box>
              </Box>
              <TextButton
                id="copyToClipboard"
                variant={TextButtonVariant.CopyToClipboard}
                onClick={copyTableToClipboard}
              />
            </Section>

            {/* Actionable Table */}
            <ActionableTable
              onCompleteTour={() => changeTab()}
              columns={actionableTableColumns}
              isTourCompleted={isTabInvitesTourCompleted ? true : false}
              tourClass="featureTour"
              joyRideTourId="joyride-programDetails-enrollmentInvitesTable-complete"
              rowActionsTourSteps={rowActionsTourSteps}
              rows={enrollmentInvites ?? []}
              noResultsMessage={t(
                'Enrollments.Invite.Table.NoInvite',
                'No Results Found'
              )}
              rowActions={rowActions.length ? rowActions : undefined}
              onCellClick={(params) => {
                const { row, field } = params
                clickableCell(row as EnrollmentInviteRowInfo, field)
              }}
              handlePaginationChange={handlePagination}
              pagination={{
                ...enrollmentInvitesPagination,
                page: enrollmentInvitesPagination.page - 1,
                orderBy: enrollmentInvitesPagination.orderBy.map(
                  (orderBy: string) => {
                    const splitQuery = orderBy.split(' ')
                    return {
                      [splitQuery[0]]:
                        (splitQuery[1] as OrderByDirection) ||
                        OrderByDirection.Asc,
                    }
                  }
                ),
              }}
            />
          </>
        ) : (
          <EmptyPage
            message={
              <Typography variant="subtitle1" component="p">
                {t(
                  'Enrollments.Invite.Table.Message.NoEnrollmentsInviteData',
                  'No families have been invited to this program'
                )}
              </Typography>
            }
          />
        )}
      </section>
    </>
  )
}

export default EnrollmentsInviteTable
